import { type MetaDescriptor } from 'react-router'
import { CONSTANTS } from '#app/constants/index.ts'

/**
 * Creates a consistent page title with the app name
 * @param pageTitle - The specific page title (e.g., "Profile", "Login")
 * @returns Formatted title string: "Page Title | App Name"
 */
export function createTitle(pageTitle: string): string {
	return `${pageTitle} | ${CONSTANTS.NAME}`
}

/**
 * Creates a meta function return value with a title
 * Useful for quick route meta exports
 * @param pageTitle - The specific page title
 * @returns Meta array with title object
 */
export function createMetaTitle(pageTitle: string) {
	return [{ title: createTitle(pageTitle) }]
}

/**
 * Removes duplicate meta descriptors from an array
 * Keeps the last occurrence of each unique meta tag based on its identifying attributes
 * @param metaDescriptors - Array of meta descriptors to deduplicate
 * @returns Deduplicated array of meta descriptors
 */
export function deduplicateMetaDescriptors(
	metaDescriptors: MetaDescriptor[],
): MetaDescriptor[] {
	const uniqueMeta = metaDescriptors.reduce((acc, meta) => {
		const key =
			'title' in meta
				? 'title'
				: 'name' in meta
					? `name:${meta.name}`
					: 'property' in meta
						? `property:${meta.property}`
						: JSON.stringify(meta)
		acc.set(key, meta)
		return acc
	}, new Map<string, MetaDescriptor>())

	return Array.from(uniqueMeta.values())
}

/**
 * Gets deduplicated parent meta from route matches
 * Useful for merging parent route meta without duplicates
 * @param matches - Array of route matches from the meta function
 * @returns Deduplicated array of parent meta descriptors
 */
export function getParentMeta(
	matches: ReadonlyArray<
		(Record<string, unknown> & { meta?: MetaDescriptor[] }) | undefined
	>,
): MetaDescriptor[] {
	const parentMeta = matches.map((match) => match?.meta ?? []).flat()
	return deduplicateMetaDescriptors(parentMeta)
}

/**
 * Creates a complete meta array with deduplicated parent meta, title, and optional additional meta tags
 * @param matches - Array of route matches from the meta function
 * @param options - Configuration object with title and optional meta tags
 * @returns Complete meta descriptor array
 *
 * @example
 * // Simple usage with just a title
 * createMeta(matches, { title: 'Home' })
 *
 * @example
 * // With additional meta tags
 * createMeta(matches, {
 *   title: 'Home',
 *   meta: [
 *     { name: 'description', content: 'Welcome to our site' },
 *     { property: 'og:image', content: '/image.png' }
 *   ]
 * })
 */
export function createMeta(
	matches: ReadonlyArray<
		(Record<string, unknown> & { meta?: MetaDescriptor[] }) | undefined
	>,
	options: { title: string; meta?: MetaDescriptor[] },
): MetaDescriptor[] {
	return deduplicateMetaDescriptors([
		...getParentMeta(matches),
		...createMetaTitle(options.title),
		...(options.meta ?? []),
	])
}
